import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { push } from 'connected-react-router'
import { common, layouts } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import Skeleton, { SkeletonVariant } from 'components/common/Skeleton'
import contentPlayerRoutes from 'modules/AdaptiveLearningContentPlayer/routes'
import { routes as requirementsRoutes } from 'modules/requirements/Requirements/routing'
import routes from './routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

type RouteState = {
  sessionId?: string
  assessmentId?: string
}

const AssessmentPlayer: React.FC = () => {
  const assessmentPlayerRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()
  const { simulatorDashboardForAdministratorsAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')
  const { state } = useLocation<RouteState>()
  const { sessionId, assessmentId } = state ?? {}

  useEffect(() => {
    const handlePostMessage = (e: MessageEvent) => {
      if (e.data.type === 'DISABLE_ADAPTIVE_LEARNING') {
        dispatch(push(`${requirementsRoutes.root}`))
      } else if (e.data.type === 'EXIT_ASSESSMENT_PLAYER') {
        dispatch(push(`${requirementsRoutes.root}?showAdaptiveLearning=true`))
      } else if (e.data.type === 'VIEW_ADAPTIVE_LEARNING_LEARNING_CONTENT') {
        dispatch(push(contentPlayerRoutes.root))
      }
    }
    window.addEventListener('message', handlePostMessage)
    const cleanupUnauthorizedEvent = handleUnauthorizedEvent(dispatch)

    return () => {
      window.removeEventListener('message', handlePostMessage)
      cleanupUnauthorizedEvent()
    }
  }, [dispatch, simulatorDashboardForAdministratorsAvailable])

  const sendTokenAndUserInfo = () => {
    if (assessmentPlayerRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      assessmentPlayerRef.current.contentWindow?.postMessage(
        {
          type: 'OLP_ASSESSMENT_PLAYER',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
        },
        window.cc.assessmentPlayer ?? ''
      )
      //? this is to hide track bars from the window
      const trackVerticalElements = document.querySelectorAll<HTMLElement>(
        '[data-selector="TrackVertical"]'
      )

      if (trackVerticalElements.length >= 2) {
        for (let i = trackVerticalElements.length - 1; i >= trackVerticalElements.length - 2; i--) {
          trackVerticalElements[i].style.visibility = 'hidden'
        }
      }
      const parentDiv = assessmentPlayerRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Assessment Player'
            ref={assessmentPlayerRef}
            src={
              sessionId
                ? `${window.cc.assessmentPlayer}/${routes.adaptiveLearningAssessment}/?sessionId=${sessionId}`
                : `${window.cc.assessmentPlayer}/?assessmentId=${assessmentId}`
            }
            onLoad={sendTokenAndUserInfo}
            width='100%'
            height='100%'
            style={{ border: 'none', overflow: 'hidden' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AssessmentPlayer
